import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isE2eAuthEnabled } from '../../utils/e2eAuth';
import {
  AuthApiError,
  buildLogoutUrl,
  isAuthMockEnabled,
  refreshPasswordlessTokens,
  revokeRefreshToken,
  SessionExpiredError,
  startPasswordless as apiStartPasswordless,
  verifyPasswordlessOtp,
} from './authApi';
import {
  AuthUser,
  isSessionUsable,
  readSession,
  StoredSession,
  tokenExpiry,
  userFromSession,
  writeSession,
} from './tokens';

// Refresh when the access token has less than this left.
const EXPIRY_LEEWAY_MS = 60_000;

export interface AuthContextValue {
  isAuthenticated: boolean;
  user: AuthUser | undefined;
  /** Set when a refresh fails with 401; `/welcome` shows a notice for it. */
  sessionExpired: boolean;
  getAccessToken: () => Promise<string>;
  startPasswordless: (email: string) => Promise<void>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  logout: (options?: { returnTo?: string }) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * With E2E auth enabled, `?e2e_token=` becomes the session (no refresh) and is
 * stripped from the URL.
 */
function takeE2eTokenFromUrl(): StoredSession | null {
  if (!isE2eAuthEnabled()) return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('e2e_token');
  if (!token) return null;

  params.delete('e2e_token');
  const search = params.toString();
  window.history.replaceState(
    {},
    '',
    window.location.pathname +
      (search ? `?${search}` : '') +
      window.location.hash
  );

  const session: StoredSession = {
    accessToken: token,
    expiresAt: tokenExpiry(token) ?? Date.now() + 60 * 60 * 1000,
    source: 'e2e',
  };
  writeSession(session);
  return session;
}

/**
 * What the @auth0/auth0-react sign-in left in localStorage: its token cache
 * (with a still-valid refresh token) and the old e2e token. Nothing reads
 * them any more.
 */
function removeLegacyAuthStorage() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('@@auth0spajs@@'))
      .forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem('e2e-auth-token');
  } catch {
    // Storage blocked: nothing to clean up.
  }
}

const readInitialSession = (): StoredSession | null => {
  removeLegacyAuthStorage();
  const session = takeE2eTokenFromUrl() ?? readSession();
  if (session && !isSessionUsable(session)) {
    writeSession(null);
    return null;
  }
  return session;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(
    readInitialSession
  );
  const [sessionExpired, setSessionExpired] = useState(false);
  const refreshInFlight = useRef<Promise<string> | null>(null);

  const queryClient = useQueryClient();

  const persist = useCallback((next: StoredSession | null) => {
    writeSession(next);
    setSession(next);
  }, []);

  // Signing in again no longer reloads the page, so when a session ends here
  // the previous user's cached queries and selected account have to go too, or
  // the next user can start on them. (Logout reloads the page, and its callers
  // clear localStorage.)
  const clearUserData = useCallback(() => {
    queryClient.clear();
    try {
      localStorage.removeItem('account');
      localStorage.removeItem('impersonationLock');
    } catch {
      // Storage blocked: nothing was saved.
    }
  }, [queryClient]);

  // Another tab signed out: storage is already empty.
  const dropSession = useCallback(() => {
    clearUserData();
    setSession(null);
  }, [clearUserData]);

  const expire = useCallback(() => {
    clearUserData();
    persist(null);
    setSessionExpired(true);
  }, [clearUserData, persist]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    // Read storage on every call: it's cheap, and it picks up a refresh or a
    // logout from another tab.
    const current = readSession();
    if (!current) {
      dropSession();
      throw new SessionExpiredError();
    }
    if (current.expiresAt - Date.now() > EXPIRY_LEEWAY_MS) {
      return current.accessToken;
    }
    if (!current.refreshToken) {
      expire();
      throw new SessionExpiredError();
    }
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }

    const { refreshToken } = current;
    refreshInFlight.current = (async () => {
      try {
        const next = await refreshPasswordlessTokens(refreshToken);
        // The session may have changed while the refresh ran: a logout must
        // not be undone, and another tab's newer tokens win.
        const stored = readSession();
        if (!stored) {
          dropSession();
          throw new SessionExpiredError();
        }
        if (stored.refreshToken !== refreshToken) {
          setSession(stored);
          return stored.accessToken;
        }
        persist(next);
        return next.accessToken;
      } catch (err) {
        if (err instanceof SessionExpiredError) throw err;
        // 401: the refresh token is no longer valid. 400 and 503 can't be
        // fixed by retrying either, so sign in again rather than leave the
        // user on pages that keep failing.
        if (
          err instanceof AuthApiError &&
          (err.status === 400 || err.status === 401 || err.status === 503)
        ) {
          expire();
          throw new SessionExpiredError();
        }
        // 502 or a network error: keep the session so the next call retries.
        throw err;
      } finally {
        refreshInFlight.current = null;
      }
    })();
    return refreshInFlight.current;
  }, [dropSession, expire, persist]);

  const startPasswordless = useCallback(
    (email: string) => apiStartPasswordless(email),
    []
  );

  const verifyOtp = useCallback(
    async (email: string, otp: string) => {
      const next = await verifyPasswordlessOtp(email, otp);
      setSessionExpired(false);
      persist(next);
    },
    [persist]
  );

  const logout = useCallback(
    (options?: { returnTo?: string }) => {
      // From state, not storage: callers clear localStorage before logging out.
      const source = session?.source;
      if (source === 'passwordless' && session?.refreshToken) {
        revokeRefreshToken(session.refreshToken);
      }
      // Only clear storage: updating state would re-render, and AuthGuard would
      // flash /welcome before the browser leaves for Auth0.
      writeSession(null);
      if (source === 'e2e' || isAuthMockEnabled()) {
        window.location.assign('/welcome');
        return;
      }
      window.location.assign(
        buildLogoutUrl(options?.returnTo || window.location.origin)
      );
    },
    [session]
  );

  const user = useMemo(
    () => (session ? userFromSession(session) : undefined),
    [session]
  );

  const value: AuthContextValue = {
    isAuthenticated: isSessionUsable(session),
    user,
    sessionExpired,
    getAccessToken,
    startPasswordless,
    verifyOtp,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
